import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Target, Eye, Compass, Shield, Check } from 'lucide-react';
import { aboutContent, subsidiaries } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SubsidiaryIcon from '../components/ui/SubsidiaryIcon';

const AboutPage = () => {
  const { language, t, pick } = useLanguage();
  useDocumentTitle(language === 'fr' ? 'À propos' : 'About');
  const e = {
    en: { aboutUs: 'About Us', companyName: 'Smartworld Group', howWeOperate: 'How We Operate', principles: 'Principles That Guide Us', exploreEco: 'Explore Our Ecosystem', exploreSub: 'Discover our eight specialized companies working in synergy.', viewEco: 'View Ecosystem' },
    fr: { aboutUs: 'À propos', companyName: 'Smartworld Group', howWeOperate: 'Comment nous opérons', principles: 'Les principes qui nous guident', exploreEco: 'Explorez notre écosystème', exploreSub: 'Découvrez nos huit entreprises spécialisées qui travaillent en synergie.', viewEco: 'Voir l\'écosystème' }
  }[language];

  return (
    <div className="pt-20">
      <section className="section-padding-lg bg-[#1a1c1b] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] border border-[#5B7A8A] rounded-full"></div>
        </div>
        <div className="container-swg relative z-10">
          <div className="max-w-4xl">
            <span className="caption text-[#5B7A8A] block mb-4">{e.aboutUs}</span>
            <h1 className="heading-1 text-white mb-8">{e.companyName}</h1>
            <div className="accent-line mb-8"></div>
            <p className="body-large max-w-2xl">{pick(aboutContent.whoWeAre.text)}</p>
          </div>
        </div>
      </section>

      <section className="section-padding-lg bg-[#302f2c]/30 border-t border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <div className="grid lg:grid-cols-2 gap-16">
            <div className="swg-card rounded-2xl p-10">
              <div className="w-16 h-16 bg-[#3d5a6a]/50 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-[#5B7A8A]" />
              </div>
              <h2 className="heading-3 text-white mb-4">{pick(aboutContent.vision.title)}</h2>
              <div className="accent-line mb-6"></div>
              <p className="body-medium text-[#9AACB8]">{pick(aboutContent.vision.text)}</p>
            </div>

            <div className="swg-card rounded-2xl p-10">
              <div className="w-16 h-16 bg-[#3d5a6a]/50 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#5B7A8A]" />
              </div>
              <h2 className="heading-3 text-white mb-4">{pick(aboutContent.mission.title)}</h2>
              <div className="accent-line mb-6"></div>
              <ul className="space-y-4">
                {pick(aboutContent.mission.points).map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#5B7A8A] flex-shrink-0 mt-0.5" />
                    <span className="text-[#9AACB8]">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding-lg bg-[#1a1c1b]">
        <div className="container-swg">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="caption text-[#5B7A8A] block mb-4">{e.howWeOperate}</span>
            <h2 className="heading-2 text-white mb-6">{pick(aboutContent.model.title)}</h2>
            <div className="accent-line mx-auto mb-8"></div>
            <p className="body-large mb-6 max-w-2xl mx-auto">{pick(aboutContent.model.intro)}</p>
          </div>

          <div className="space-y-6">
            {subsidiaries.map((sub, index) => (
              <Link
                key={sub.id}
                to={`/subsidiary/${sub.id}`}
                className="swg-card rounded-2xl p-8 lg:p-10 block group hover:border-[#5B7A8A]/50 transition-all"
                data-testid={`about-ecosystem-card-${sub.id}`}
              >
                <div className="flex flex-col lg:flex-row gap-8 items-start lg:items-center">
                  <div className="flex items-center gap-6 lg:w-1/3">
                    <SubsidiaryIcon subsidiary={sub} size="lg" />
                    <div>
                      <span className="text-[#9AACB8] text-sm font-mono">0{index + 1}</span>
                      <h3 className="text-white font-bold text-2xl leading-tight">{sub.name}</h3>
                    </div>
                  </div>
                  <div className="lg:flex-1">
                    <p className="font-semibold mb-3" style={{ color: sub.color }}>{sub.i18n[language].tagline}</p>
                    <p className="text-[#9AACB8] text-sm line-clamp-2 leading-relaxed">{sub.i18n[language].about}</p>
                  </div>
                  <div className="lg:w-32 flex items-center justify-end">
                    <div className="flex items-center gap-2 font-semibold opacity-60 group-hover:opacity-100 transition-all group-hover:translate-x-1" style={{ color: sub.color }}>
                      <span className="text-sm">{t('cta.discover')}</span>
                      <ArrowUpRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding-lg bg-[#302f2c]/30 border-t border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="caption text-[#5B7A8A] block mb-4">{pick(aboutContent.governance.title)}</span>
            <h2 className="heading-2 text-white mb-6">{e.principles}</h2>
            <div className="accent-line mx-auto mb-8"></div>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {pick(aboutContent.governance.points).map((point, index) => {
              const icons = [Compass, Shield, Eye, Target, Link];
              const Icon = icons[index] || Shield;
              return (
                <div key={index} className="swg-card rounded-xl p-6 text-center">
                  <div className="w-14 h-14 bg-[#3d5a6a]/50 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-[#5B7A8A]" />
                  </div>
                  <h4 className="text-white font-semibold text-sm">{point}</h4>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#1a1c1b] border-t border-[rgba(91,122,138,0.3)]">
        <div className="container-swg">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="heading-3 text-white mb-2">{e.exploreEco}</h2>
              <p className="text-[#9AACB8]">{e.exploreSub}</p>
            </div>
            <Link to="/ecosystem" className="btn-primary">
              {e.viewEco}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
